import { useEffect, useRef, useState } from 'react';
import { ScoutingClient } from './scoutingClient';

export interface BackgroundMetadata {
  src: string;
  aspectRatio: number;
  isLoadable: boolean;
}

const getAspectRatio = (width: number, height: number) => {
  if (!width || !height) return 1;
  return width / height;
};

const loadBackgroundMetadata = async (sources: string[]): Promise<BackgroundMetadata[]> => {
  if (!sources.length) return [];

  const loaders = sources.map(
    (src) =>
      new Promise<BackgroundMetadata>((resolve) => {
        const image = new Image();
        image.onload = () => {
          resolve({
            src,
            aspectRatio: getAspectRatio(image.naturalWidth, image.naturalHeight),
            isLoadable: true,
          });
        };
        image.onerror = () =>
          resolve({
            src,
            aspectRatio: 1,
            isLoadable: false,
          });
        image.src = src;
      })
  );

  return Promise.all(loaders);
};

const pickBackgroundForViewport = (
  metadata: BackgroundMetadata[],
  viewportWidth: number,
  viewportHeight: number
): string | null => {
  if (!metadata.length) return null;

  const loadableMetadata = metadata.filter((item) => item.isLoadable);
  if (!loadableMetadata.length) return null;

  const viewportAspect = getAspectRatio(viewportWidth, viewportHeight);
  const preferLandscape = viewportAspect >= 1;
  const preferred = loadableMetadata.filter((item) =>
    preferLandscape ? item.aspectRatio >= 1 : item.aspectRatio < 1
  );
  const pool = preferred.length ? preferred : loadableMetadata;
  const selection = pool[Math.floor(Math.random() * pool.length)];
  return selection?.src ?? null;
};

export const useUnauthBackground = (enabled: boolean) => {
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [hasBackgrounds, setHasBackgrounds] = useState<boolean>(false);
  const metadataRef = useRef<BackgroundMetadata[] | null>(null);
  const selectedBackgroundRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setBackgroundUrl(null);
      selectedBackgroundRef.current = null;
      return;
    }

    let cancelled = false;

    const selectBackground = () => {
      if (selectedBackgroundRef.current) {
        setBackgroundUrl(selectedBackgroundRef.current);
        return;
      }
      const metadata = metadataRef.current ?? [];
      const selected = pickBackgroundForViewport(metadata, window.innerWidth, window.innerHeight);
      selectedBackgroundRef.current = selected;
      if (!cancelled) {
        setBackgroundUrl(selected);
      }
    };

    const initialize = async () => {
      if (!metadataRef.current) {
        const backgroundPayload = await ScoutingClient.listPublicBackgroundUrls();
        if (!backgroundPayload.urls.length) {
          const debugPayload = await ScoutingClient.listPublicBackgroundUrls({ debug: true });
          console.warn('[unauthBackgrounds] no public background URLs returned', {
            header: backgroundPayload.debugHeader || debugPayload.debugHeader || null,
            debug: debugPayload.debug || null,
          });
        } else if (backgroundPayload.debugHeader) {
          console.info('[unauthBackgrounds] public background debug', backgroundPayload.debugHeader);
        }
        metadataRef.current = await loadBackgroundMetadata(backgroundPayload.urls);
        setHasBackgrounds((metadataRef.current?.some((item) => item.isLoadable) ?? false));
      }
      selectBackground();
    };

    initialize().catch((err) => {
      console.warn('[unauthBackgrounds] failed to load public background photos', err);
      if (!cancelled) {
        setHasBackgrounds(false);
        setBackgroundUrl(null);
      }
    });
    window.addEventListener('resize', selectBackground);

    return () => {
      cancelled = true;
      window.removeEventListener('resize', selectBackground);
    };
  }, [enabled]);

  return { backgroundUrl, hasBackgrounds };
};
